
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Share2, Activity } from 'lucide-react';
import { AppShell } from '../components/Layout';
import {
  ExaminationTable, AnalysisSummaryCard, TissueCompositionCard,
  WoundStageCard, ImageModal,
} from '../components/ResultComponents';
import { API_URL, API_KEY } from '../config';

function parseStage(raw) {
  if (!raw) return [];
  let val = raw;
  if (typeof val === 'string') {
    try { val = JSON.parse(val); } catch { return []; }
  }
  if (Array.isArray(val)) val = val[0] ?? val;
  if (Array.isArray(val) && val[0]?.Stage !== undefined) return val;
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    return Object.entries(val).map(([Stage, Confidence]) => ({
      Stage,
      Confidence: typeof Confidence === 'number' ? Confidence : parseFloat(Confidence) || 0,
    }));
  }
  if (Array.isArray(val) && typeof val[0] === 'string') {
    return val.map(e => {
      const [Stage, conf] = e.split(':').map(x => x.trim());
      return { Stage: Stage || 'Unknown', Confidence: parseFloat(conf) || 0 };
    });
  }
  return [];
}

function safeJSON(val) {
  if (!val) return [];
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return []; }
}

export default function HistoryScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient_id, timestamp, result: passedResult } = location.state || {};

  const [resultData, setResultData] = useState(passedResult || null);
  const [loading,    setLoading]    = useState(!passedResult);
  const [showImage,  setShowImage]  = useState(false);

  useEffect(() => {
    if (passedResult) return;
    const fetchResult = async () => {
      if (!patient_id) { setLoading(false); return; }
      try {
        const res = await fetch(`${API_URL}/api/results/${patient_id}`, {
          headers: { 'Cytometrix-API-KEY': API_KEY },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data?.visits && Array.isArray(data.visits)) {
          const visit = timestamp
            ? data.visits.find(v => v.timestamp === timestamp)
            : data.visits[0];
          setResultData(visit || data.visits[0] || null);
        } else if (data?.timestamp) {
          setResultData(data);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchResult();
  }, [patient_id, timestamp, passedResult]);

  const date = useMemo(() => {
    if (!resultData?.timestamp) return '';
    return new Date(resultData.timestamp).toLocaleDateString();
  }, [resultData]);

  const time = useMemo(() => {
    if (!resultData?.timestamp) return '';
    return new Date(resultData.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, [resultData]);

  const reportList  = useMemo(() => safeJSON(resultData?.Analysis || resultData?.analysis), [resultData]);
  const woundTissue = useMemo(() => safeJSON(resultData?.Tissue   || resultData?.tissue),   [resultData]);
  const woundStage  = useMemo(() => parseStage(resultData?.Stage  || resultData?.stage),    [resultData]);

  const analysisId  = resultData?.AnalysisID || resultData?.analysisId || 'N/A';
  const imgUrl      = String(resultData?.Img_URL || resultData?.img_url || 'N/A');
  const patientName = resultData?.full_name  || 'N/A';
  const displayId   = resultData?.patient_id || patient_id || 'N/A';

  if (loading) {
    return (
      <AppShell title="Analysis History">
        <div
          style={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 24px',
          }}
        >
          <div className="loading-wrap" style={{ textAlign: 'center' }}>
            <div className="spinner spinner-lg" />
            <span>Loading visit record…</span>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!resultData) {
    return (
      <AppShell title="Analysis History">
        <div
          style={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 24px',
          }}
        >
          <div className="loading-wrap" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
              Visit record not found.
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const sharePayload = {
    Analysis:   resultData?.Analysis || resultData?.analysis,
    Tissue:     resultData?.Tissue   || resultData?.tissue,
    Stage:      woundStage,
    Img_URL:    imgUrl,
    timestamp:  resultData.timestamp,
    AnalysisID: analysisId,
    full_name:  patientName,
    patient_id: displayId,
  };

  return (
    <AppShell title="Analysis History">
      {/* ── Centered page wrapper ── */}
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px 60px',
        }}
      >
        {/* ── Constrained content column ── */}
        <div style={{ width: '100%', maxWidth: 780 }}>

          {/* ── Page header ── */}
          <div
            className="page-header"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
              marginBottom: 28,
            }}
          >
            <div>
              <h1 className="page-title" style={{ marginBottom: 4 }}>Visit Record</h1>
              <p className="page-subtitle" style={{ margin: 0 }}>
                {patientName} · {date}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={15} /> Dashboard
              </button>
              <button
                className="btn btn-outline"
                onClick={() => navigate('/share-history', { state: { result: sharePayload } })}
              >
                <Share2 size={15} /> Share
              </button>
              <button className="btn btn-primary" onClick={() => navigate('/new-analysis')}>
                <Activity size={15} /> New Analysis
              </button>
            </div>
          </div>

          {/* ── Result sections ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ExaminationTable
              patientName={patientName}
              patientId={displayId}
              date={date}
              time={time}
              analysisId={analysisId}
              imgUrl={imgUrl !== 'N/A' ? imgUrl : null}
              onImageClick={() => setShowImage(true)}
            />
            <AnalysisSummaryCard reportList={reportList} />
            <TissueCompositionCard woundTissue={woundTissue} />
            <WoundStageCard woundStageData={woundStage} />
          </div>

        </div>
      </div>

      {showImage && imgUrl !== 'N/A' && (
        <ImageModal imgUrl={imgUrl} onClose={() => setShowImage(false)} />
      )}
    </AppShell>
  );
}
